import React, { useState, useEffect } from "react";
import { Box, Typography, keyframes, Icon, Divider } from "@mui/material";
import { useTheme } from "@emotion/react";

// Staggered fade-in animation for calendar items
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animation for second calendar sliding into first
const slideIntoFirst = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(-48px);
    opacity: 0;
  }
`;

// Animation for placeholder bars during collapse
const fadeOutPlaceholder = keyframes`
  0% {
    opacity: 1;
    transform: scaleX(1) translateX(0);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(0.7) translateX(-8px);
  }
  100% {
    opacity: 0;
    transform: scaleX(0.2) translateX(-20px);
  }
`;

// Animation for first calendar transforming to folder
const transformToFolder = keyframes`
  0% {
    background-color: rgba(158, 158, 158, 0.8);
  }
  100% {
    background-color: rgba(255, 193, 7, 0.8);
  }
`;

// Animation for third calendar transforming to folder (different color)
const transformToFolderOrange = keyframes`
  0% {
    background-color: rgba(129, 199, 132, 0.8);
  }
  100% {
    background-color: rgba(255, 152, 0, 0.8);
  }
`;

const SmartOrganizationIcon = ({ variant = "light" }) => {
  const theme = useTheme();
  const { primary, overlay } = theme.palette;
  const isDark = variant === "dark";
  const [animationPhase, setAnimationPhase] = useState(0);

  // Colors based on variant - all grayscale
  const placeholderColor = isDark ? overlay.white30 : "rgba(255, 255, 255, 0.3)";
  const checkboxColors = [
    "rgba(158, 158, 158, 0.8)", // Grey
    "rgba(100, 181, 246, 0.8)", // Blue
    "rgba(129, 199, 132, 0.8)", // Green
    "rgba(239, 154, 154, 0.8)", // Red
  ];
  const edgeLineColor = "rgba(255, 255, 255, 0.3)";
  const backgroundColor = isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.4)";

  const calendarItems = Array.from({ length: 4 }, (_, index) => ({
    id: index,
    width: index === 0 ? "70%" : index === 1 ? "50%" : index === 2 ? "85%" : "60%",
    delay: index * 0.2,
  }));

  // Start grouping animations with proper timing
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setAnimationPhase(1); // Start first grouping animation (calendars 1 & 2)
    }, 2500);

    const timer2 = setTimeout(() => {
      setAnimationPhase(2); // Start second grouping animation (calendars 3 & 4)
    }, 3500); // 1 second after first grouping

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: "200px",
        height: "140px",
        margin: "0 auto",
        backgroundColor: backgroundColor,
        backdropFilter: "blur(10px)",
        display: "flex",
        flexDirection: "column",
        padding: "16px",
      }}
    >
      {/* Left edge line */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />
      
      {/* Right edge line */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />

      {/* Calendar List */}
      <Box sx={{ 
        flex: 1, 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center",
        py: "8px"
      }}>
        {calendarItems.map((item, index) => {
          // Skip rendering calendars that have been grouped
          if ((index === 1 && animationPhase >= 1) || (index === 3 && animationPhase >= 2)) return null;

          return (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                mb: index === calendarItems.length - 1 ? 0 : "12px",
                minHeight: "24px",
                opacity: 0,
                animation: `${fadeInUp} 0.6s ease-out ${item.delay}s both`,
                // Add slide animation for second calendar (into first)
                ...(index === 1 && animationPhase >= 1 && {
                  animation: `${fadeInUp} 0.6s ease-out ${item.delay}s both, ${slideIntoFirst} 0.5s ease-out 0s both`,
                }),
                // Add slide animation for fourth calendar (into third)
                ...(index === 3 && animationPhase >= 2 && {
                  animation: `${fadeInUp} 0.6s ease-out ${item.delay}s both, ${slideIntoFirst} 0.5s ease-out 0s both`,
                }),
              }}
            >
              {/* Calendar Checkbox */}
              <Box
                sx={{
                  position: "relative",
                  width: "24px",
                  height: "24px",
                  backgroundColor: checkboxColors[index % checkboxColors.length],
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  // Transform first calendar to folder when first animation starts
                  ...(index === 0 && animationPhase >= 1 && {
                    animation: `${fadeInUp} 0.6s ease-out ${item.delay}s both, ${transformToFolder} 0.3s ease-out 0.2s both`,
                  }),
                  // Transform third calendar to folder when second animation starts
                  ...(index === 2 && animationPhase >= 2 && {
                    animation: `${fadeInUp} 0.6s ease-out ${item.delay}s both, ${transformToFolderOrange} 0.3s ease-out 0.2s both`,
                  }),
                }}
              >
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{
                    fontSize: "16px",
                    color: "white",
                    fontWeight: 600,
                  }}
                >
                  {((index === 0 && animationPhase >= 1) || (index === 2 && animationPhase >= 2)) ? "folder" : "check"}
                </Icon>
              </Box>

              {/* Calendar name placeholder */}
              <Box
                sx={{
                  height: "8px",
                  backgroundColor: placeholderColor,
                  borderRadius: "4px",
                  width: item.width,
                  transformOrigin: "left center",
                  transition: "all 0.3s ease-out",
                  // Add smooth fade animation for placeholder bars during collapse
                  ...((index === 1 && animationPhase >= 1) && {
                    animation: `${fadeOutPlaceholder} 0.5s cubic-bezier(0.4, 0.0, 0.2, 1) 0s both`,
                  }),
                  ...((index === 3 && animationPhase >= 2) && {
                    animation: `${fadeOutPlaceholder} 0.5s cubic-bezier(0.4, 0.0, 0.2, 1) 0s both`,
                  }),
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SmartOrganizationIcon; 